import type {
  ApplyFiltersResponse,
  ApplyMappingsResponse,
  BulkUpdateItem,
  DataImport,
  DataImportBusinessPreview,
  DataImportFilterOptionsResponse,
  DataImportHouseholdPreview,
  DataImportProgress,
  DataImportReviewItemsResponse,
  DataImportsResponse,
  FilterSelection,
  ImportRemainingAs,
  WizardStep,
} from "@/schema/data-import"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export function fetchDataImports(context: ApiContext) {
  return apiContextFetch<DataImportsResponse>(context, "data_imports").then((r) => r.data_imports)
}

export function fetchDataImport(context: ApiContext, id: string) {
  return apiContextFetch<DataImport>(context, `data_imports/${id}`)
}

export function fetchDataImportProgress(context: ApiContext, id: string) {
  return apiContextFetch<DataImportProgress>(context, `data_imports/${id}/progress`)
}

export function fetchFilterOptions(context: ApiContext, dataImportId: string) {
  return apiContextFetch<DataImportFilterOptionsResponse>(context, `data_imports/${dataImportId}/filter_options`).then(
    (r) => r.filter_options
  )
}

export function fetchReviewItems(context: ApiContext, dataImportId: string) {
  return apiContextFetch<DataImportReviewItemsResponse>(context, `data_imports/${dataImportId}/review_items`)
}

export function bulkUpdateReviewItems(context: ApiContext, dataImportId: string, updates: BulkUpdateItem[]) {
  return apiContextFetch<void>(context, `data_imports/${dataImportId}/bulk_updates`, {
    method: "POST",
    body: JSON.stringify({ updates }),
  })
}

export function applyFilters(
  context: ApiContext,
  dataImportId: string,
  params: { import_mode: "all" | "filtered"; import_filters?: FilterSelection[] }
) {
  return apiContextFetch<ApplyFiltersResponse>(context, `data_imports/${dataImportId}/filters`, {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export function applyMappings(
  context: ApiContext,
  dataImportId: string,
  params: {
    client_filters?: FilterSelection[]
    past_client_filters?: FilterSelection[]
    prospect_filters?: FilterSelection[]
    import_remaining_items_as?: ImportRemainingAs
  }
) {
  return apiContextFetch<ApplyMappingsResponse>(context, `data_imports/${dataImportId}/mappings`, {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export function fetchHouseholdPreview(context: ApiContext, dataImportId: string, householdId: number) {
  return apiContextFetch<DataImportHouseholdPreview>(context, `data_imports/${dataImportId}/households/${householdId}`)
}

export function fetchBusinessPreview(context: ApiContext, dataImportId: string, businessId: number) {
  return apiContextFetch<DataImportBusinessPreview>(context, `data_imports/${dataImportId}/businesses/${businessId}`)
}

export function updateDataImport(context: ApiContext, dataImportId: string, params: { wizard_step: WizardStep }) {
  return apiContextFetch<DataImport>(context, `data_imports/${dataImportId}`, {
    method: "PATCH",
    body: JSON.stringify({ data_import: params }),
  })
}

export function startImport(context: ApiContext, dataImportId: string) {
  return apiContextFetch<DataImport>(context, `data_imports/${dataImportId}/start`, {
    method: "POST",
  })
}

export function submitTwoFactorCode(context: ApiContext, dataImportId: string, code: string) {
  return apiContextFetch<DataImport>(context, `data_imports/${dataImportId}/two_factor_authentication`, {
    method: "POST",
    body: JSON.stringify({ code }),
  })
}

export async function uploadFileToImport(
  context: ApiContext,
  dataImportId: string,
  file: File,
  getRefreshedContext?: () => Promise<ApiContext | null>
) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")

  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const signedId = await uploadFileDirectly(file, apiRoot, context.token)

  const freshContext = getRefreshedContext ? ((await getRefreshedContext()) ?? context) : context

  return apiContextFetch<DataImport>(freshContext, `data_imports/${dataImportId}/file_upload`, {
    method: "POST",
    body: JSON.stringify({ import_file: signedId }),
  })
}
